import type React from 'react'

import { useState, useRef } from 'react'
import { Download } from 'lucide-react'
import CircleLoader from '@strata-monorepo/shared-ui/components/CircleLoader/CircleLoader'
import { Slider } from '../Slider/Slider'
import { cn } from '@strata-monorepo/shared-ui/utils/cn'
import MiniEntry from '../BillEntry/MiniEntry'
import BillUploadEntry from '../BillUploadEntry'
import type { BillEntryData } from '@strata-monorepo/shared-ui/components/BillEntry/BillEntry'
import EntryForm, {
  type EntryFormDisabledField,
} from '@strata-monorepo/shared-ui/components/BillEntry/EntryForm'

const DEFAULT_SLIDER_VALUE = 1000
const SLIDER_MIN = 500
const SLIDER_MAX = 4400
const SLIDER_STEP = 50

export interface EnergyBillFileUploadProps {
  className?: string
  onChange?: (files: FileList | File[]) => void
  onClick?: () => void
  progress?: number
  showUploadSection?: boolean
  sliderValue?: number
  onSliderChange?: (value: number) => void
  uploadedBills?: BillEntryData[]
  requiredFields?: readonly string[]
  onUpdateUploadedBills?: (entries: BillEntryData[]) => void
  onDeleteUploadedBill?: (entryIndex: number) => void
  onSubmitUploadedEdit?: (billData: BillEntryData, fieldSource: string) => void
  onSliderInteracted?: () => void
  isMobile?: boolean
  /** Passed to `EntryForm` when editing an uploaded bill (e.g. hide plan name or day). */
  entryFormDisabledFields?: readonly EntryFormDisabledField[]
}

export const EnergyBillFileUpload = ({
  className,
  onChange,
  onClick,
  progress = 0,
  showUploadSection = false,
  sliderValue,
  onSliderChange,
  uploadedBills = [],
  requiredFields = [],
  onUpdateUploadedBills,
  onDeleteUploadedBill,
  onSubmitUploadedEdit,
  onSliderInteracted,
  isMobile = false,
  entryFormDisabledFields,
}: EnergyBillFileUploadProps) => {
  const [editingEntryIndex, setEditingEntryIndex] = useState<number | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploadZoneHovered, setIsUploadZoneHovered] = useState(false)
  const [localSliderValue, setLocalSliderValue] = useState(DEFAULT_SLIDER_VALUE)
  const [isKwhInputFocused, setIsKwhInputFocused] = useState(false)
  const [kwhInputString, setKwhInputString] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const sliderInteractedRef = useRef(false)
  const disableUploadBill = false // TODO: Remove this once the upload bill is implemented
  const displayValue = sliderValue ?? localSliderValue
  const isControlled = sliderValue !== undefined

  const addFiles = (files: FileList | File[]): void => {
    if (onChange) {
      onChange(files)
    }
  }

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault()
    setIsDragOver(false)
    if (hasErrorInUploadedBills) return

    const files = e.dataTransfer.files
    if (files.length > 0) {
      addFiles(files)
    }
  }

  const handleFileSelect = (): void => {
    if (hasErrorInUploadedBills) return
    onClick?.()
    fileInputRef.current?.click()
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (hasErrorInUploadedBills) return
    const files = e.target.files
    if (files && files.length > 0) {
      addFiles(files)
    }
    // Reset input value to allow selecting the same file again
    e.target.value = ''
  }

  const handleSliderValueChange = (values: number[]): void => {
    if (!sliderInteractedRef.current) {
      sliderInteractedRef.current = true
      onSliderInteracted?.()
    }
    const next = values[0] ?? DEFAULT_SLIDER_VALUE
    if (!isControlled) {
      setLocalSliderValue(next)
    }
    onSliderChange?.(next)
  }

  const handleKwhInputFocus = (): void => {
    setIsKwhInputFocused(true)
    setKwhInputString(String(displayValue))
  }

  const handleKwhInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!sliderInteractedRef.current) {
      sliderInteractedRef.current = true
      onSliderInteracted?.()
    }
    const raw = e.target.value.replace(/\D/g, '')
    setKwhInputString(raw)
    const parsed = raw === '' ? SLIDER_MIN : parseInt(raw, 10)
    if (!Number.isNaN(parsed)) {
      if (!isControlled) {
        setLocalSliderValue(parsed)
      }
      onSliderChange?.(parsed)
    }
  }

  const handleKwhInputBlur = (): void => {
    setKwhInputString('')
    setIsKwhInputFocused(false)
  }

  const handleKwhInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    }
  }

  const handleCancelEdit = (): void => {
    setEditingEntryIndex(null)
  }

  const handleSubmitUploadedEdit = (billData: BillEntryData, fieldSource: string): void => {
    onSubmitUploadedEdit?.(billData, fieldSource)
    setEditingEntryIndex(null)
  }

  const hasErrorInUploadedBills = uploadedBills.some((bill) => Boolean(bill.error))

  const kwhDisplayValue = isKwhInputFocused ? kwhInputString : displayValue.toLocaleString()

  const currentKwhValue = isKwhInputFocused
    ? kwhInputString === ''
      ? displayValue
      : (parseInt(kwhInputString, 10) ?? displayValue)
    : displayValue
  const parsedCurrent = Number(currentKwhValue)
  const hasKwhError =
    !Number.isNaN(parsedCurrent) && (parsedCurrent < SLIDER_MIN || parsedCurrent > SLIDER_MAX)
  const kwhErrorMessage =
    hasKwhError && parsedCurrent > SLIDER_MAX
      ? `Usage cannot exceed ${SLIDER_MAX.toLocaleString()} kWh.`
      : hasKwhError && parsedCurrent < SLIDER_MIN
        ? `Usage cannot be less than ${SLIDER_MIN.toLocaleString()} kWh.`
        : null

  return (
    <div className={cn('strata:gap-y-4 strata:flex strata:flex-col strata:md:gap-y-6', className)}>
      <input
        ref={fileInputRef}
        type='file'
        multiple
        accept='.pdf,.jpeg,.jpg,.png'
        onChange={handleFileInputChange}
        className='strata:hidden'
        disabled={hasErrorInUploadedBills}
      />

      <div className='strata:flex strata:flex-col-reverse strata:gap-y-7 strata:md:flex-row strata:gap-x-4 strata:w-full strata:m-0 strata:py-2 strata:md:py-4'>
        <div className='strata:flex strata:flex-col-reverse strata:md:flex-col strata:items-center strata:justify-center strata:gap-y-2 strata:w-full'>
          <div className='strata:flex strata:w-full strata:justify-between strata:text-body-xs'>
            <span>&lt;{SLIDER_MIN.toLocaleString()} kWh</span>
            <span>{SLIDER_MAX.toLocaleString()}+ kWh</span>
          </div>
          <div className='strata:w-11/12 strata:self-center'>
            <Slider
              value={[displayValue]}
              max={SLIDER_MAX}
              min={SLIDER_MIN}
              step={SLIDER_STEP}
              onValueChange={handleSliderValueChange}
            />
          </div>
        </div>
        <div className='strata:flex strata:flex-col strata:gap-y-1 strata:md:w-[160px]'>
          <div
            className={`strata:text-center strata:flex strata:justify-center strata:py-3 strata:px-4 strata:items-center strata:border strata:rounded-sm strata:font-bold strata:whitespace-nowrap ${
              hasKwhError ? 'strata:border-system-negative-accent' : 'strata:border-gray-400'
            }`}
          >
            <span className='strata:flex strata:items-baseline strata:gap-1'>
              <input
                type='text'
                inputMode='numeric'
                aria-label='Monthly usage in kWh'
                aria-invalid={hasKwhError}
                aria-describedby={hasKwhError ? 'kwh-error-message' : undefined}
                value={kwhDisplayValue}
                onChange={handleKwhInputChange}
                onFocus={handleKwhInputFocus}
                onBlur={handleKwhInputBlur}
                onKeyDown={handleKwhInputKeyDown}
                className={`strata:text-title-base strata:font-bold strata:max-w-[70px] strata:bg-transparent strata:border-none strata:outline-none strata:text-center strata:p-0 [&::-webkit-outer-spin-button]:strata:appearance-none [&::-webkit-inner-spin-button]:strata:appearance-none ${
                  hasKwhError ? 'strata:text-system-negative-foreground' : ''
                }`}
              />
              <span className='strata:text-sm strata:shrink-0'>kWh</span>
            </span>
          </div>
          {kwhErrorMessage && (
            <p
              id='kwh-error-message'
              role='alert'
              className='strata:text-system-negative-foreground strata:text-body-xs strata:text-left'
            >
              {kwhErrorMessage}
            </p>
          )}
        </div>
      </div>
      <div className='strata:flex strata:flex-col strata:gap-y-2'>
        {uploadedBills.length === 0 && (
          <>
            <span className='strata:text-body-xs strata:text-gray-600 strata:text-left'>
              Have your energy bill handy? (optional)
            </span>
            <div
              className={cn(
                'strata:rounded-sm strata:p-4 strata:text-center strata:transition-colors',
                hasErrorInUploadedBills
                  ? 'strata:bg-gray-50 strata:cursor-not-allowed strata:opacity-60'
                  : isDragOver
                    ? 'strata:bg-secondary-50 strata:cursor-pointer'
                    : 'strata:cursor-pointer strata:hover:bg-secondary-50',
                hasErrorInUploadedBills || isDragOver || isUploadZoneHovered
                  ? 'strata:bg-file-upload-accent'
                  : 'strata:bg-file-upload',
              )}
              role='button'
              tabIndex={hasErrorInUploadedBills ? -1 : 0}
              aria-disabled={hasErrorInUploadedBills}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleFileSelect}
              onKeyDown={handleFileSelect}
              onMouseEnter={() => {
                if (!hasErrorInUploadedBills) setIsUploadZoneHovered(true)
              }}
              onMouseLeave={() => setIsUploadZoneHovered(false)}
            >
              <div
                className={cn(
                  'strata:flex strata:gap-3 strata:items-center strata:items-center strata:space-y-2',
                  progress > 0 ? 'strata:justify-center' : '',
                )}
              >
                {progress === 0 && (
                  <>
                    <Download
                      className='strata:w-4 strata:h-4 strata:text-secondary-400 !strata:mb-0'
                      style={{ marginBottom: 0 }}
                    />
                    <p className='strata:text-gray-600 strata:text-xs strata:font-bold'>
                      {hasErrorInUploadedBills
                        ? 'Remove the failed upload above to add another bill'
                        : "Upload last month's bill"}
                    </p>
                  </>
                )}
                {progress > 0 && <CircleLoader percentage={progress} />}
              </div>
            </div>
          </>
        )}
        <div className='strata:flex strata:flex-col strata:gap-3'>
          {uploadedBills.map((entry, entryIndex) => (
            <div
              key={entry.fileName ?? entryIndex}
              className={
                entry.isSubmitted && entryIndex === editingEntryIndex
                  ? 'strata:bg-secondary-50 strata:px-4 strata:py-3 strata:border-gray-500 strata:flex strata:flex-col strata:gap-4 strata:rounded-primary strata:border strata:w-full'
                  : ''
              }
            >
              {entryIndex === editingEntryIndex ? (
                <>
                  <MiniEntry
                    index={entryIndex}
                    entry={entry}
                    onEdit={() => setEditingEntryIndex(entryIndex)}
                    onDelete={() => onDeleteUploadedBill?.(entryIndex)}
                  />
                  <EntryForm
                    index={entryIndex}
                    entry={entry}
                    entries={uploadedBills}
                    allEntries={uploadedBills}
                    onChange={onUpdateUploadedBills}
                    isMobile={isMobile}
                    onCancelEdit={handleCancelEdit}
                    isEditing
                    requiredFields={requiredFields}
                    onSubmit={handleSubmitUploadedEdit}
                    type='upload'
                    disabledFields={entryFormDisabledFields}
                  />
                </>
              ) : (
                <BillUploadEntry
                  entry={entry}
                  entryIndex={entryIndex}
                  onEdit={() => setEditingEntryIndex(entryIndex)}
                  onDelete={() => onDeleteUploadedBill?.(entryIndex)}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
